import type { PromptInputFilePart } from '@/components/ai-elements/prompt-input'
import {
  buildSelectedEmailsDataPart,
  type SelectedEmailMetadata,
} from '@/lib/chat-message-metadata'
import { CHAT_LAUNCH_HANDOFF_TTL_MS } from '@/lib/shared/chat-launch-handoff'
import { discardPendingPromptFiles } from './pending-prompt-inline-files'
import { PENDING_PROMPT_KEY, type PendingPrompt } from './types'

export function toStoredFiles(files: Array<PromptInputFilePart>) {
  return files.map((file) => ({
    url: file.url,
    mediaType: file.mediaType || 'application/octet-stream',
    ...(file.filename ? { filename: file.filename } : {}),
    ...(file.previewText ? { previewText: file.previewText } : {}),
    ...(file.isHtmlAttachment ? { isHtmlAttachment: true } : {}),
  }))
}

export function buildPendingPrompt(args: {
  chatId: string
  orgId: string
  text: string
  customBrandId?: string | null
  files: Array<PromptInputFilePart>
  selectedEmails?: ReadonlyArray<SelectedEmailMetadata>
  awaitingAuth?: boolean
  messageId?: string
  remixSourceFlowSlug?: string
  /** From `stashPendingPromptFiles`, when inline files were stashed. */
  filesKey?: string
}): PendingPrompt {
  const storedFiles = args.files.length > 0 ? toStoredFiles(args.files) : []
  const selectedEmails = args.selectedEmails
    ? buildSelectedEmailsDataPart(args.selectedEmails)?.data.emails
    : undefined

  return {
    chatId: args.chatId,
    orgId: args.orgId,
    text: args.text,
    ...(args.awaitingAuth ? { awaitingAuth: true } : {}),
    ...(storedFiles.length > 0 ? { files: storedFiles } : {}),
    ...(args.filesKey ? { filesKey: args.filesKey } : {}),
    ...(selectedEmails && selectedEmails.length > 0 ? { selectedEmails } : {}),
    ...(args.customBrandId ? { customBrandId: args.customBrandId } : {}),
    ...(args.messageId ? { messageId: args.messageId } : {}),
    ...(args.remixSourceFlowSlug
      ? { remixSourceFlowSlug: args.remixSourceFlowSlug }
      : {}),
    createdAt: Date.now(),
  }
}

// ---------------------------------------------------------------------------
// Pending-prompt store (localStorage, one slot PER CHAT)
//
// The launcher hand-off contract: the composer writes the prompt and
// navigates immediately (`/home` → `/chat/<id>`); the chat thread that
// mounts there reads the slot, paints the user message optimistically in
// the same frame, and fires the orchestrator POST. The slot survives the
// navigation (and, for the `awaitingAuth` variant, the Clerk sign-up
// round-trip), so localStorage rather than React state.
//
// Slots are keyed by chatId so two tabs launching chats near-
// simultaneously can never overwrite each other's prompt. The
// `awaitingAuth` variant ALSO mirrors into the legacy single slot
// (`PENDING_PROMPT_KEY`) because the post-signup redirect effect in the
// launch composer discovers the pending chat by reading that slot
// without knowing the chatId.
//
// Unlike the autonav arm, a slot is NOT cleared on read: it lives until
// the live Convex snapshot contains the message (`clearPendingPrompt`) or
// the TTL expires, so a reload in the submit window can replay instead of
// landing on an empty chat. The `submitted` flag distinguishes "replay
// must wait for the live snapshot" from "first arrival, fire away".
//
// All functions are SSR-safe and never throw.
// ---------------------------------------------------------------------------

/**
 * How long a pending prompt stays replayable: generous enough for a slow
 * sign-up hand-off, small enough that a stale slot can never re-submit into
 * a later visit. Entries written before `createdAt` existed (the legacy
 * awaitingAuth flow) stay replayable for the thread that finds them — they
 * predate the replay semantics and are one-shot anyway — but they never
 * satisfy {@link hasPendingPrompt}, whose answer must stay TTL-bounded.
 */
const PENDING_PROMPT_TTL_MS = CHAT_LAUNCH_HANDOFF_TTL_MS

const PENDING_PROMPT_KEY_PREFIX = `${PENDING_PROMPT_KEY}:`

function chatSlotKey(chatId: string): string {
  return `${PENDING_PROMPT_KEY_PREFIX}${chatId}`
}

/**
 * Change notification for the slots, so a render-time reader
 * (`useSyncExternalStore` in the chat panel) re-reads when a launcher stages
 * a prompt, the thread marks it submitted, or the live transcript clears it.
 * Writes from ANOTHER tab arrive through the DOM `storage` event, which this
 * subscription also listens to.
 */
const listeners = new Set<() => void>()

function emitPendingPromptChange(): void {
  for (const listener of listeners) {
    listener()
  }
}

export function subscribePendingPrompts(listener: () => void): () => void {
  listeners.add(listener)
  const onStorage = (event: StorageEvent) => {
    if (event.key === null || event.key.startsWith(PENDING_PROMPT_KEY)) {
      listener()
    }
  }
  if (typeof window !== 'undefined') {
    window.addEventListener('storage', onStorage)
  }
  return () => {
    listeners.delete(listener)
    if (typeof window !== 'undefined') {
      window.removeEventListener('storage', onStorage)
    }
  }
}

function getLocalStorage(): Storage | null {
  try {
    if (typeof window === 'undefined') {
      return null
    }
    return window.localStorage ?? null
  } catch {
    return null
  }
}

function isStoredSelectedEmail(value: unknown): value is SelectedEmailMetadata {
  if (!(value && typeof value === 'object')) {
    return false
  }
  const candidate = value as Record<string, unknown>
  const isValidVersion =
    candidate.version === undefined ||
    candidate.version === 'latest' ||
    (typeof candidate.version === 'number' &&
      Number.isInteger(candidate.version) &&
      candidate.version >= 0)
  return (
    typeof candidate.emailId === 'string' &&
    candidate.emailId.length > 0 &&
    typeof candidate.title === 'string' &&
    (candidate.imagePngUrl === undefined ||
      candidate.imagePngUrl === null ||
      typeof candidate.imagePngUrl === 'string') &&
    (candidate.emailVersionId === undefined ||
      (typeof candidate.emailVersionId === 'string' &&
        candidate.emailVersionId.length > 0)) &&
    isValidVersion &&
    (candidate.automationId === undefined ||
      typeof candidate.automationId === 'string')
  )
}

type StoredPendingFile = NonNullable<PendingPrompt['files']>[number]

function isStoredPendingFile(value: unknown): value is StoredPendingFile {
  if (!(value && typeof value === 'object')) {
    return false
  }
  const candidate = value as Record<string, unknown>
  return (
    typeof candidate.url === 'string' &&
    candidate.url.length > 0 &&
    typeof candidate.mediaType === 'string' &&
    candidate.mediaType.length > 0 &&
    (candidate.filename === undefined ||
      typeof candidate.filename === 'string') &&
    (candidate.previewText === undefined ||
      typeof candidate.previewText === 'string') &&
    (candidate.isHtmlAttachment === undefined ||
      typeof candidate.isHtmlAttachment === 'boolean')
  )
}

function parsePendingEntry(raw: string | null): PendingPrompt | null {
  if (!raw) {
    return null
  }
  try {
    const parsed = JSON.parse(raw) as unknown
    if (!(parsed && typeof parsed === 'object')) {
      return null
    }
    const candidate = parsed as Record<string, unknown>
    if (
      typeof candidate.chatId !== 'string' ||
      typeof candidate.orgId !== 'string' ||
      typeof candidate.text !== 'string'
    ) {
      return null
    }
    const files = Array.isArray(candidate.files)
      ? candidate.files.filter(isStoredPendingFile)
      : []
    const selectedEmails = Array.isArray(candidate.selectedEmails)
      ? candidate.selectedEmails.filter(isStoredSelectedEmail)
      : []
    return {
      chatId: candidate.chatId,
      orgId: candidate.orgId,
      text: candidate.text,
      ...(typeof candidate.modelId === 'string'
        ? { modelId: candidate.modelId }
        : {}),
      ...(typeof candidate.awaitingAuth === 'boolean'
        ? { awaitingAuth: candidate.awaitingAuth }
        : {}),
      ...(files.length > 0 ? { files } : {}),
      ...(typeof candidate.filesKey === 'string'
        ? { filesKey: candidate.filesKey }
        : {}),
      ...(typeof candidate.customBrandId === 'string'
        ? { customBrandId: candidate.customBrandId }
        : {}),
      ...(selectedEmails.length > 0 ? { selectedEmails } : {}),
      ...(typeof candidate.messageId === 'string'
        ? { messageId: candidate.messageId }
        : {}),
      ...(typeof candidate.remixSourceFlowSlug === 'string'
        ? { remixSourceFlowSlug: candidate.remixSourceFlowSlug }
        : {}),
      ...(typeof candidate.createdAt === 'number' &&
      Number.isFinite(candidate.createdAt)
        ? { createdAt: candidate.createdAt }
        : {}),
      ...(typeof candidate.submitted === 'boolean'
        ? { submitted: candidate.submitted }
        : {}),
    }
  } catch {
    return null
  }
}

function isExpired(pending: PendingPrompt): boolean {
  return (
    typeof pending.createdAt === 'number' &&
    Date.now() - pending.createdAt >= PENDING_PROMPT_TTL_MS
  )
}

/** Drop expired per-chat slots so abandoned launches don't accumulate. */
function pruneExpiredSlots(storage: Storage): void {
  try {
    const staleKeys: Array<string> = []
    for (let index = 0; index < storage.length; index += 1) {
      const key = storage.key(index)
      if (!key?.startsWith(PENDING_PROMPT_KEY_PREFIX)) {
        continue
      }
      const pending = parsePendingEntry(storage.getItem(key))
      if (!pending || isExpired(pending)) {
        staleKeys.push(key)
        // The slot is the only pointer to the stashed inline files —
        // drop them with it, or an abandoned launch leaks megabytes of
        // IndexedDB forever.
        void discardPendingPromptFiles(pending?.filesKey)
      }
    }
    for (const key of staleKeys) {
      storage.removeItem(key)
    }
  } catch {
    // ignore
  }
}

export function writePendingPrompt(pending: PendingPrompt): void {
  const storage = getLocalStorage()
  if (!storage) {
    return
  }
  try {
    pruneExpiredSlots(storage)
    const serialized = JSON.stringify(pending)
    storage.setItem(chatSlotKey(pending.chatId), serialized)
    if (pending.awaitingAuth) {
      // Legacy single slot — the post-signup redirect discovers the
      // pending chat here (it has no chatId of its own to key on).
      storage.setItem(PENDING_PROMPT_KEY, serialized)
    }
    emitPendingPromptChange()
  } catch {
    // ignore (quota / disabled storage)
  }
}

/**
 * The ONE slot-precedence rule both readers follow: the per-chat slot wins,
 * and the legacy single slot counts only when there is no per-chat slot and
 * it actually holds THIS chat. Pure — expiry is reported, never cleared, so
 * a render-time caller can use it.
 */
function peekPendingEntry(
  storage: Storage,
  chatId: string
): { key: string; pending: PendingPrompt } | null {
  const fromChatSlot = parsePendingEntry(storage.getItem(chatSlotKey(chatId)))
  if (fromChatSlot) {
    return { key: chatSlotKey(chatId), pending: fromChatSlot }
  }
  const fromLegacySlot = parsePendingEntry(storage.getItem(PENDING_PROMPT_KEY))
  if (!fromLegacySlot || fromLegacySlot.chatId !== chatId) {
    return null
  }
  return { key: PENDING_PROMPT_KEY, pending: fromLegacySlot }
}

/**
 * Read the pending prompt for `chatId`. Returns `null` when no slot
 * exists, the entry is malformed or expired, or (legacy fallback) the
 * single slot belongs to a different chat. Expired slots are lazily
 * cleared as a side effect of the read.
 */
export function readPendingPrompt(chatId: string): PendingPrompt | null {
  if (typeof chatId !== 'string' || chatId.length === 0) {
    return null
  }
  const storage = getLocalStorage()
  if (!storage) {
    return null
  }
  try {
    const entry = peekPendingEntry(storage, chatId)
    if (!entry) {
      // An expired legacy slot is cleared whichever chat it names, so a
      // stale awaitingAuth mirror never outlives its TTL.
      const legacy = parsePendingEntry(storage.getItem(PENDING_PROMPT_KEY))
      if (legacy && isExpired(legacy)) {
        void discardPendingPromptFiles(legacy.filesKey)
        storage.removeItem(PENDING_PROMPT_KEY)
        emitPendingPromptChange()
      }
      return null
    }
    if (isExpired(entry.pending)) {
      void discardPendingPromptFiles(entry.pending.filesKey)
      storage.removeItem(entry.key)
      emitPendingPromptChange()
      return null
    }
    return entry.pending
  } catch {
    return null
  }
}

/**
 * Render-safe presence check: true when an unexpired prompt staged for
 * `orgId` waits for `chatId`. It is the chat panel's proof that this device
 * launched a chat whose row does not exist yet, so it follows
 * {@link readPendingPrompt}'s slot precedence but never writes storage (no
 * lazy expiry removal), and an entry without `createdAt` never counts — the
 * access exception it grants stays bounded by the TTL.
 */
export function hasPendingPrompt(args: {
  chatId: string | null
  orgId: string | null
}): boolean {
  const { chatId, orgId } = args
  if (!(chatId && orgId)) {
    return false
  }
  const storage = getLocalStorage()
  if (!storage) {
    return false
  }
  try {
    const entry = peekPendingEntry(storage, chatId)
    const pending = entry?.pending
    return (
      pending !== undefined &&
      pending.orgId === orgId &&
      typeof pending.createdAt === 'number' &&
      !isExpired(pending)
    )
  } catch {
    return false
  }
}

/**
 * Mark the chat's prompt as submitted (orchestrator POST fired). No-op
 * when no slot holds this chat's prompt.
 */
export function markPendingPromptSubmitted(chatId: string): void {
  const storage = getLocalStorage()
  if (!storage) {
    return
  }
  const pending = readPendingPrompt(chatId)
  if (!pending) {
    return
  }
  try {
    const serialized = JSON.stringify({ ...pending, submitted: true })
    storage.setItem(chatSlotKey(chatId), serialized)
    const legacy = parsePendingEntry(storage.getItem(PENDING_PROMPT_KEY))
    if (legacy?.chatId === chatId) {
      storage.setItem(PENDING_PROMPT_KEY, serialized)
    }
    emitPendingPromptChange()
  } catch {
    // ignore
  }
}

/**
 * Clear the slot for `chatId`. The legacy single slot is only removed
 * when it holds THIS chat's prompt (so clearing chat A can never strip
 * chat B's in-flight prompt).
 */
export function clearPendingPrompt(chatId: string): void {
  const storage = getLocalStorage()
  if (!storage) {
    return
  }
  try {
    const slot = parsePendingEntry(storage.getItem(chatSlotKey(chatId)))
    if (slot?.filesKey) {
      void discardPendingPromptFiles(slot.filesKey)
    }
    storage.removeItem(chatSlotKey(chatId))
    const legacy = parsePendingEntry(storage.getItem(PENDING_PROMPT_KEY))
    if (legacy?.chatId === chatId) {
      storage.removeItem(PENDING_PROMPT_KEY)
    }
    emitPendingPromptChange()
  } catch {
    // ignore
  }
}
